// Public passport (PRD §4.8, US-15 to US-18, T22, T28): no sign-in, no cookie, no storage, no third-party request.
// Reads the public_passport view only. Language by the viewer or ?lang=, never by location (FPR Art. 11b(4)).
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { fmtValue, Loading } from '../components/ui.tsx';
import { LANGUAGES } from '../i18n/index.ts';
import { publicClient } from '../lib/supabase.ts';
import type { PublicPassport } from '@w2g/engine';

interface Row {
  upi: string;
  prod_code: string;
  demonstration: boolean;
  environment: string;
  regime: string;
  published_at: string;
  public_payload: PublicPassport;
}

export function Passport() {
  const { t, i18n } = useTranslation();
  const { upi = '' } = useParams();
  const [row, setRow] = useState<Row | null | undefined>(undefined);
  const [qr, setQr] = useState('');

  useEffect(() => {
    void (async () => {
      const { data } = await publicClient().from('public_passport').select('*').eq('upi', upi).maybeSingle();
      setRow((data as Row | null) ?? null);
      setQr(
        await QRCode.toString(`${window.location.origin}/p/${encodeURIComponent(upi)}`, {
          type: 'svg',
          margin: 1,
        }),
      );
    })();
  }, [upi]);

  if (row === undefined) return <Loading />;
  if (row === null)
    return (
      <main className="passport">
        <p>{t('passport.not_found')}</p>
      </main>
    );
  const p = row.public_payload;
  return (
    <main className={`passport${row.demonstration ? ' watermark' : ''}`} lang={i18n.language}>
      <nav className="lang">
        {t('passport.language')}:{' '}
        {LANGUAGES.slice(0, 2).map((l) => (
          <button key={l} aria-pressed={i18n.language === l} onClick={() => void i18n.changeLanguage(l)}>
            {l.toUpperCase()}
          </button>
        ))}
      </nav>
      <h1>{t('passport.title')}</h1>
      {row.demonstration && (
        <p className="stamp" role="note">
          {t('passport.demo')}
        </p>
      )}
      <p>
        <strong>{p.upi}</strong> · {row.prod_code} · {t('col.batch')} {p.batch_id}
      </p>
      <p>
        <span className="muted">
          {p.classification.candidate ? t('classification.candidate') : t('classification.confirmed')}:
        </span>{' '}
        <strong>{p.classification.reg_route}</strong> ({p.classification.route_confidence})
      </p>
      <p>
        <strong>{p.ce_marking === 'marked' ? t('passport.ce.marked') : t('passport.ce.not_marked')}</strong>
      </p>
      <dl>
        {p.items.map((it) => (
          <div key={it.dpp_field_id}>
            <dt>{it.label}</dt>
            <dd>{it.values.map((v) => `${fmtValue(v.value)}${v.unit ? ` ${v.unit}` : ''}`).join(' · ')}</dd>
          </div>
        ))}
      </dl>
      <figure>
        <div className="qr" aria-label={t('passport.qr')} dangerouslySetInnerHTML={{ __html: qr }} />
        <figcaption className="small muted">{t('passport.qr')}</figcaption>
      </figure>
      <footer className="small muted">
        {t('passport.regime')}: {row.regime} · {t('passport.published')} {row.published_at} ·{' '}
        {t('footer.version', { engine: p.versions.engine, pack: p.versions.rule_pack })}
      </footer>
    </main>
  );
}
